import type { AuctisyncSupabase } from './client.js';

/**
 * Module #9 — the card hold (pre-authorisation) a bidder must place before
 * bidding on an auction whose house requires one.
 *
 * WHY THIS EXISTS AND WHY IT IS URGENT. `place_bid` refuses with
 * `reason: 'preauth_missing'` when `auction.requires_cc_preauth` is set and the
 * bidder has no `authorized` row in `cc_preauths`. The website handles that: it
 * shows a panel on the lot page, takes a card, places the hold, and the bidder
 * bids. The native app rendered the refusal MESSAGE — "Add a card on file to
 * finish activating your approval" — and had no way to act on it. Nothing in
 * the app called `create-cc-preauth` at all.
 *
 * A bidder therefore read that instruction, went to Payment Methods, saved a
 * card, came back, and was refused again — because a saved payment method is
 * not a hold. The app told them to do something that could not work. That is
 * worse than a missing feature; it is a dead end with a confident signpost.
 *
 * Latent rather than live only because no auction currently sets the flag, and
 * DTA has `require_bidder_card_on_file = false`. It is one toggle on the
 * onboarding wizard away from breaking bidding in the app.
 *
 * THE FLOW, identical on both surfaces:
 *   1. `create-setup-intent`  → client secret + the tenant's Connect account id
 *   2. collect the card (Stripe Elements on web, PaymentSheet on native) —
 *      this is the ONLY step that differs, because the SDKs differ
 *   3. `create-cc-preauth`    → the manual-capture PaymentIntent, the real hold
 *
 * Step 2 is why this module carries no UI: everything either side of it is
 * shared, and the middle is genuinely platform-specific.
 */

/** An active hold, as a bidder should see it. */
export interface ExistingPreauth {
  id: string;
  amountCents: number;
  /** Null when the hold has no explicit expiry. */
  expiresAt: string | null;
}

export type FetchPreauthResult =
  | { ok: true; preauth: ExistingPreauth | null }
  | { ok: false; message: string };

/** Postgrest's "no rows" for `maybeSingle` — not an error worth surfacing. */
const NO_ROWS = 'PGRST116';

/**
 * The bidder's active hold on this auction, or null.
 *
 * Only `authorized` counts. A released or failed row means they must place a
 * new hold, and treating one as current would let the UI say "card on file"
 * while `place_bid` kept refusing — the two disagreeing is precisely the
 * confusion this module exists to end.
 */
export async function fetchExistingPreauth(
  supabase: AuctisyncSupabase,
  params: { auctionId: string; userId: string },
): Promise<FetchPreauthResult> {
  const { data, error } = await supabase
    .from('cc_preauths')
    .select('id, amount_cents, expires_at')
    .eq('auction_id', params.auctionId)
    .eq('user_id', params.userId)
    .eq('status', 'authorized')
    .maybeSingle();

  if (error && error.code !== NO_ROWS) return { ok: false, message: error.message };
  if (!data) return { ok: true, preauth: null };

  const row = data as { id?: unknown; amount_cents?: unknown; expires_at?: unknown };
  return {
    ok: true,
    preauth: {
      id: typeof row.id === 'string' ? row.id : '',
      amountCents: typeof row.amount_cents === 'number' ? row.amount_cents : 0,
      expiresAt: typeof row.expires_at === 'string' ? row.expires_at : null,
    },
  };
}

export interface SetupIntentDetails {
  clientSecret: string;
  stripeAccountId: string;
  /** What the hold will be for. Null when the server did not say. */
  preauthAmountCents: number | null;
}

export type StartSetupResult =
  | { ok: true; setup: SetupIntentDetails }
  | { ok: false; message: string };

/**
 * Ask the server to begin card setup on the tenant's Connect account.
 *
 * Both the client secret AND the account id are required. A client secret used
 * against the wrong Stripe account fails in a way that reads like a card
 * decline, so a partial response is refused here rather than surfaced as a
 * payment problem.
 */
export async function startPreauthSetup(
  supabase: AuctisyncSupabase,
  params: { auctionId: string },
): Promise<StartSetupResult> {
  const { data, error } = (await supabase.functions.invoke('create-setup-intent', {
    body: { auction_id: params.auctionId },
  })) as {
    data: {
      client_secret?: string;
      stripe_account_id?: string;
      preauth_amount_cents?: number;
      error?: string;
    } | null;
    error: { message?: string } | null;
  };

  if (!data?.client_secret || !data?.stripe_account_id) {
    return {
      ok: false,
      message: data?.error ?? error?.message ?? 'Could not start card setup.',
    };
  }

  return {
    ok: true,
    setup: {
      clientSecret: data.client_secret,
      stripeAccountId: data.stripe_account_id,
      preauthAmountCents: data.preauth_amount_cents ?? null,
    },
  };
}

export type PreauthStatus = 'authorized' | 'requires_action' | 'failed';

export type ConfirmPreauthResult =
  | { ok: true; status: PreauthStatus; preauthId: string; clientSecret?: string }
  | { ok: false; message: string };

/**
 * Place the actual hold, once a card has been saved by the SetupIntent.
 *
 * `setupIntentId` is optional to the server but should always be sent: it is
 * validated against Stripe and persisted onto the row, giving an auditable link
 * from a hold on someone's card back to the moment they consented to it.
 * Omitting it stores null and loses that trail.
 */
export async function confirmPreauth(
  supabase: AuctisyncSupabase,
  params: { auctionId: string; paymentMethodId: string; setupIntentId?: string },
): Promise<ConfirmPreauthResult> {
  const { data, error } = (await supabase.functions.invoke('create-cc-preauth', {
    body: {
      auction_id: params.auctionId,
      stripe_payment_method: params.paymentMethodId,
      ...(params.setupIntentId ? { setup_intent_id: params.setupIntentId } : {}),
    },
  })) as {
    data: {
      preauth_id?: string;
      status?: PreauthStatus;
      client_secret?: string;
      error?: string;
    } | null;
    error: { message?: string } | null;
  };

  if (!data?.status || !data?.preauth_id) {
    return { ok: false, message: data?.error ?? error?.message ?? 'Could not place the hold.' };
  }

  return {
    ok: true,
    status: data.status,
    preauthId: data.preauth_id,
    ...(data.client_secret ? { clientSecret: data.client_secret } : {}),
  };
}

/**
 * What to tell a bidder about a hold outcome.
 *
 * Shared so the two surfaces cannot describe the same Stripe result
 * differently — this is money language, and "we've held $50" meaning different
 * things on a phone and a laptop is exactly the divergence the money-path rule
 * forbids.
 */
export function describePreauthStatus(status: PreauthStatus): {
  tone: 'success' | 'notice' | 'error';
  text: string;
} {
  switch (status) {
    case 'authorized':
      return { tone: 'success', text: "Your card is on file. You're clear to bid." };
    case 'requires_action':
      // The bank wants a challenge. Not a failure, and telling a bidder their
      // card was refused here would be false.
      return {
        tone: 'notice',
        text: 'Your bank needs to confirm this hold. Follow the prompt to finish.',
      };
    case 'failed':
      return {
        tone: 'error',
        text: 'That card was refused for the hold. Try a different card.',
      };
  }
}

export type RequiresPreauthResult =
  | { ok: true; requiresPreauth: boolean }
  | { ok: false; message: string };

/**
 * Does this auction require a card hold before bidding?
 *
 * Read proactively so a bidder is told BEFORE they choose an amount and tap
 * Bid. Waiting for `place_bid` to refuse would technically work — the refusal
 * carries the right reason — but being told "you cannot bid" only after
 * deciding what to pay is the kind of small humiliation that loses a bidder.
 *
 * Fails CLOSED to `false`. If this read errors, the panel stays hidden and the
 * bidder finds out from `place_bid` instead, which is a worse experience but a
 * correct one. Failing open would show a card-collection panel on auctions that
 * never asked for one — asking someone for card details they do not owe.
 */
export async function fetchAuctionRequiresPreauth(
  supabase: AuctisyncSupabase,
  auctionId: string,
): Promise<RequiresPreauthResult> {
  const { data, error } = await supabase
    .from('auctions')
    .select('requires_cc_preauth')
    .eq('id', auctionId)
    .maybeSingle();

  if (error && error.code !== NO_ROWS) return { ok: false, message: error.message };

  const row = data as { requires_cc_preauth?: unknown } | null;
  return { ok: true, requiresPreauth: row?.requires_cc_preauth === true };
}
